import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ArrowLeft, Lightbulb,
  RefreshCw, Refrigerator, ChevronRight, Loader2,
  Thermometer, Leaf, Info
} from "lucide-react";
import TipCard from "@/components/TipCard";
import { LIKED_TIPS_KEY, normalizeLikedTip, tipKeyOf } from "@/lib/likedTips";
import { isLocked } from "@/utils/subscription";
import { LockOverlay } from "@/components/LockBadge";

// ─── Ingredient pill ──────────────────────────────────────────────────────────
function IngredientPill({ item, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 px-3 py-1.5 rounded-full font-body text-sm font-semibold transition-all border ${
        active
          ? "bg-[#7B9E7F] text-white border-[#7B9E7F] shadow-sm"
          : "bg-white text-[#6B705C] border-[#E8E5E0] hover:border-[#7B9E7F]/40"
      }`}
    >
      {item.name}
    </button>
  );
}

// ─── Ingredient detail panel ──────────────────────────────────────────────────
function IngredientDetail({ item, info, loading }) {
  const { t } = useLanguage();
  if (!item) return (
    <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8">
      <Leaf className="w-10 h-10 text-[#C4C9BC] mb-3" strokeWidth={1.5} />
      <p className="font-body text-sm text-[#A5A58D]">{t("selectIngredientHint") || "Обери інгредієнт щоб побачити деталі"}</p>
    </div>
  );

  if (loading) return (
    <div className="p-4 space-y-3 animate-pulse">
      <div className="h-5 bg-[#E8E5E0] rounded-full w-2/3" />
      <div className="grid grid-cols-4 gap-2">
        {[1,2,3,4].map(i => <div key={i} className="h-14 bg-[#E8E5E0] rounded-xl" />)}
      </div>
      <div className="h-3 bg-[#E8E5E0] rounded-full w-full" />
      <div className="h-3 bg-[#E8E5E0] rounded-full w-4/5" />
      <div className="h-3 bg-[#E8E5E0] rounded-full w-3/4" />
    </div>
  );

  if (!info) return (
    <div className="flex items-center justify-center h-full">
      <p className="font-body text-sm text-[#A5A58D]">{t("noInfoAvailable") || "Інформація недоступна"}</p>
    </div>
  );

  return (
    <div className="p-4 overflow-y-auto h-full">
      <p className="font-body font-bold text-[#3D4035] mb-3">{item.name}</p>

      {/* Macros */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: t("kcal") || "ккал", value: info.calories_per_100g },
          { label: t("protein") || "Білки", value: info.protein ? `${info.protein}г` : "—" },
          { label: t("carbs") || "Вуглев.", value: info.carbs ? `${info.carbs}г` : "—" },
          { label: t("fat") || "Жири", value: info.fat ? `${info.fat}г` : "—" },
        ].map(m => (
          <div key={m.label} className="bg-[#F5F3EF] rounded-xl p-2 text-center">
            <p className="font-body font-bold text-[#3D4035] text-sm">{m.value ?? "—"}</p>
            <p className="font-body text-[10px] text-[#A5A58D]">{m.label}</p>
          </div>
        ))}
      </div>

      {/* Vitamins */}
      {info.vitamins?.length > 0 && (
        <div className="mb-3">
          <p className="font-body text-xs text-[#A5A58D] uppercase tracking-wider mb-1.5">{t("vitamins") || "Вітаміни"}</p>
          <div className="flex flex-wrap gap-1.5">
            {info.vitamins.map(v => (
              <span key={v} className="px-2 py-0.5 bg-[#7B9E7F]/10 text-[#4A6B4E] font-body text-xs font-semibold rounded-full">
                {v}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Benefits */}
      {info.benefits && (
        <div className="mb-3">
          <p className="font-body text-xs text-[#A5A58D] uppercase tracking-wider mb-1">{t("benefits") || "Користь"}</p>
          <p className="font-body text-xs text-[#6B705C] leading-relaxed">{info.benefits}</p>
        </div>
      )}

      {/* Storage */}
      {info.storage_tip && (
        <div className="bg-[#FEF9EC] border border-[#E9C46A]/30 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Thermometer className="w-3.5 h-3.5 text-[#8B6914]" strokeWidth={1.5} />
            <p className="font-body text-xs font-semibold text-[#8B6914]">{t("storageTip") || "Зберігання"}</p>
            {info.shelf_life && <span className="ml-auto font-body text-[10px] text-[#8B6914] bg-[#E9C46A]/20 px-2 py-0.5 rounded-full">{info.shelf_life}</span>}
          </div>
          <p className="font-body text-xs text-[#6B705C] leading-relaxed">{info.storage_tip}</p>
        </div>
      )}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function DailyTipsPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { t, lang } = useLanguage();
  const locked = isLocked(user);

  const [tips, setTips] = useState([]);
  const [tipsLoading, setTipsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedTipIdx, setExpandedTipIdx] = useState(null);
  const [likedTips, setLikedTips] = useState(() => {
    try {
      const stored = localStorage.getItem(LIKED_TIPS_KEY);
      return stored ? JSON.parse(stored).map(normalizeLikedTip) : [];
    } catch { return []; }
  });

  const toggleLikeTip = (tipIdx) => {
    const tip = tips[tipIdx];
    const tipKey = tipKeyOf(tip);
    const wasLiked = likedTips.some((t) => tipKeyOf(t) === tipKey);
    setLikedTips((prev) => {
      const newLiked = wasLiked
        ? prev.filter((t) => tipKeyOf(t) !== tipKey)
        : [...prev, { type: tip?.type, title: tip?.title, message: tip?.message ?? null }];
      localStorage.setItem(LIKED_TIPS_KEY, JSON.stringify(newLiked));
      return newLiked;
    });
    if (!wasLiked) {
      api().post("/tips/like", { type: tip?.type, title: tip?.title, message: tip?.message }).catch(() => {});
    }
  };

  const isTipLiked = (tip) => {
    const tipKey = tipKeyOf(tip);
    return likedTips.some((t) => tipKeyOf(t) === tipKey);
  };

  const [fridgeItems, setFridgeItems] = useState([]);
  const [fridgeLoading, setFridgeLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [ingredientInfo, setIngredientInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(false);
  const infoCache = useRef({});

  useEffect(() => {
    fetchTips();
    fetchFridge();
  }, [lang]);

  const fetchTips = async (refresh = false) => {
    if (isLocked(user)) { setTipsLoading(false); return; }  // не-підписник: не викликаємо гейтнутий /ai/daily-tips (уникаємо 402)
    if (refresh) setRefreshing(true);
    else setTipsLoading(true);
    try {
      const res = await api().get("/ai/daily-tips" + (refresh ? "?refresh=true" : ""));
      setTips(res.data?.tips || []);
    } catch {}
    setTipsLoading(false);
    setRefreshing(false);
  };

  const fetchFridge = async (retries = 1) => {
    if (isLocked(user)) { setFridgeLoading(false); return; }  // не-підписник: не тягнемо холодильник/ingredient-info (щоб інфа не витікала повз paywall)
    try {
      const res = await api().get("/fridge");
      const items = res.data || [];
      setFridgeItems(items);
      if (items.length > 0) selectIngredient(items[0]);
      setFridgeLoading(false);
    } catch (err) {
      console.error("DailyTips fetchFridge failed", err);
      if (retries > 0) setTimeout(() => fetchFridge(retries - 1), 800);
      else setFridgeLoading(false);
    }
  };

  const selectIngredient = async (item) => {
    setSelectedItem(item);
    if (infoCache.current[item.name]) {
      setIngredientInfo(infoCache.current[item.name]);
      return;
    }
    setIngredientInfo(null);
    setInfoLoading(true);
    try {
      const res = await api().get(`/ai/ingredient-info/${encodeURIComponent(item.name)}`);
      infoCache.current[item.name] = res.data;
      setIngredientInfo(res.data);
    } catch {}
    setInfoLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] pb-28" data-testid="daily-tips-page" onClick={() => setExpandedTipIdx(null)}>

      {/* Header */}
      <div className="pl-[72px] pr-5 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white border border-[#E8E5E0] flex items-center justify-center">
          <ArrowLeft className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
        </button>
        <h1 className="font-heading text-xl text-[#3D4035] flex-1">{t("dailyTips")}</h1>
        <button
          onClick={() => fetchTips(true)}
          disabled={refreshing}
          className="w-10 h-10 rounded-full bg-[#7B9E7F]/10 flex items-center justify-center hover:bg-[#7B9E7F]/20 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 text-[#7B9E7F] ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
        </button>
      </div>

      {/* ── Section 1: Tips carousel ── */}
      <div className="mb-6">
        <div className="px-5 mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg text-[#3D4035]">{t("tipsForYou") || "Поради для вас"}</h2>
          {tips.length > 0 && <span className="font-body text-xs text-[#A5A58D]">{tips.length} {t("tipsAvailable") || "порад"}</span>}
        </div>

        {locked ? (
          <div className="px-5">
            <LockOverlay>
              <div className="flex flex-col gap-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="bg-white rounded-2xl p-4 border border-[#E8E5E0]">
                    <div className="h-3 w-24 bg-[#E8E5E0] rounded-full mb-2" />
                    <div className="h-3 w-3/4 bg-[#EBE9E4] rounded-full mb-1.5" />
                    <div className="h-3 w-2/3 bg-[#EBE9E4] rounded-full" />
                  </div>
                ))}
              </div>
            </LockOverlay>
          </div>
        ) : tipsLoading ? (
          <div className="px-5 flex gap-3 overflow-hidden">
            {[1,2,3].map(i => (
              <div key={i} className="shrink-0 w-44 h-40 rounded-2xl bg-[#E8E5E0] animate-pulse" />
            ))}
          </div>
        ) : tips.length === 0 ? (
          <div className="px-5 text-center py-8">
            <Lightbulb className="w-10 h-10 text-[#C4C9BC] mx-auto mb-2" strokeWidth={1.5} />
            <p className="font-body text-sm text-[#A5A58D]">{t("tipsEmptyState")}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 px-5">
            {tips.map((tip, i) => (
              <TipCard key={i} tip={tip} idx={i}
                expanded={expandedTipIdx === i}
                onExpand={(idx) => setExpandedTipIdx(expandedTipIdx === idx ? null : idx)}
                liked={isTipLiked(tip)}
                onToggleLike={toggleLikeTip}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Ingredients skeleton while fridge loads ── */}
      {!locked && fridgeLoading && fridgeItems.length === 0 && (
        <div className="px-5 mb-6">
          <div className="flex gap-2 mb-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-8 w-24 rounded-full bg-[#E8E5E0] animate-pulse" />
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5E0] p-4 space-y-3 animate-pulse min-h-[200px]">
            <div className="h-5 bg-[#E8E5E0] rounded-full w-2/3" />
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-14 bg-[#E8E5E0] rounded-xl" />)}
            </div>
            <div className="h-3 bg-[#E8E5E0] rounded-full w-full" />
            <div className="h-3 bg-[#E8E5E0] rounded-full w-4/5" />
          </div>
        </div>
      )}

      {/* ── Section 2: Ingredients ── */}
      {!locked && fridgeItems.length > 0 && (
        <div className="mb-6">
          <div className="px-5 mb-3">
            <h2 className="font-heading text-lg text-[#3D4035]">{t("ingredients") || "Інгредієнти"}</h2>
            <p className="font-body text-xs text-[#A5A58D] mt-0.5">{t("ingredientsTipHint") || "Натисни на інгредієнт щоб дізнатись більше"}</p>
          </div>

          {/* Pills */}
          <div className="flex gap-2 px-5 overflow-x-auto scrollbar-hide pb-1 mb-3">
            {fridgeItems.map(item => (
              <IngredientPill
                key={item.id}
                item={item}
                active={selectedItem?.id === item.id}
                onClick={() => selectIngredient(item)}
              />
            ))}
          </div>

          {/* Detail panel */}
          <div className="mx-5 bg-white rounded-2xl border border-[#E8E5E0] shadow-[0_2px_12px_rgb(0,0,0,0.04)] min-h-[200px]">
            <IngredientDetail item={selectedItem} info={ingredientInfo} loading={infoLoading} />
          </div>
        </div>
      )}

      {/* ── Section 3: Storage tip for selected ingredient ── */}
      {!locked && fridgeItems.length > 0 && (
        <div className="px-5 mb-6">
          <h2 className="font-heading text-lg text-[#3D4035] mb-3">{t("storageSection") || "Поради зі зберігання"}</h2>
          {infoLoading ? (
            <div className="bg-white rounded-2xl p-4 border border-[#E8E5E0] animate-pulse">
              <div className="h-4 bg-[#E8E5E0] rounded-full w-1/3 mb-2" />
              <div className="h-3 bg-[#E8E5E0] rounded-full w-full mb-1.5" />
              <div className="h-3 bg-[#E8E5E0] rounded-full w-4/5" />
            </div>
          ) : selectedItem && ingredientInfo?.storage_tip ? (
            <div className="bg-[#FEF9EC] rounded-2xl p-4 border border-[#E9C46A]/40 flex gap-3 items-start shadow-[0_1px_6px_rgb(0,0,0,0.03)]">
              <div className="w-9 h-9 rounded-full bg-white/80 flex items-center justify-center shrink-0">
                <Thermometer className="w-4 h-4 text-[#8B6914]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-body font-semibold text-sm text-[#8B6914]">{selectedItem.name}</p>
                  {ingredientInfo.shelf_life && (
                    <span className="font-body text-[10px] text-[#8B6914] bg-[#E9C46A]/20 px-2 py-0.5 rounded-full">{ingredientInfo.shelf_life}</span>
                  )}
                </div>
                <p className="font-body text-xs text-[#6B705C] leading-relaxed">{ingredientInfo.storage_tip}</p>
              </div>
            </div>
          ) : selectedItem ? (
            <div className="bg-white rounded-2xl p-4 border border-[#E8E5E0] text-center">
              <p className="font-body text-sm text-[#A5A58D]">{t("noInfoAvailable") || "Інформація недоступна"}</p>
            </div>
          ) : null}
        </div>
      )}

    </div>
  );
}
